import { apiService } from './api.service';
import { AdminDashboard, CompanyDashboard } from 'types/dashboard';
import type {
  ScanAnalytics,
  ScanAnalyticsPeriod,
  ScanAnalyticsScope,
  RecentScansFilters,
  RecentScansPage,
} from 'types/scanAnalytics';
import { normalizeScanAnalytics, normalizeRecentScansPage } from 'utils/scanAnalyticsNormalize';

class DashboardService {
  async getAccountDashboard(): Promise<CompanyDashboard> {
    const response = await apiService.get<CompanyDashboard>(`/dashboard/Account`);
    const data = (response as any)?.data || response;
    if (!data) throw new Error((response as any)?.message || 'Failed to load account dashboard');
    return data as CompanyDashboard;
  }

  async getAdminDashboard(): Promise<AdminDashboard> {
    const response = await apiService.get<AdminDashboard>(`/dashboard/Admin`);
    const data = (response as any)?.data || response;
    if (!data) throw new Error((response as any)?.message || 'Failed to load admin dashboard');
    return data as AdminDashboard;
  }

  async getScanAnalytics(
    period: ScanAnalyticsPeriod = 'daily',
    scope?: ScanAnalyticsScope
  ): Promise<ScanAnalytics> {
    const params = { period };
    const response = scope?.employeeScopeId
      ? await apiService.get<ScanAnalytics>(
          `/employee/${scope.employeeScopeId}/scan-analytics`,
          { params }
        )
      : scope?.companyScopeId
        ? await apiService.get<ScanAnalytics>(
            `/company/${scope.companyScopeId}/scan-analytics`,
            { params }
          )
        : scope?.individualScopeId
          ? await apiService.get<ScanAnalytics>(
              `/individual/${scope.individualScopeId}/scan-analytics`,
              { params }
            )
          : await apiService.get<ScanAnalytics>(`/dashboard/ScanAnalytics`, { params });
    const data = (response as any)?.data || response;
    return normalizeScanAnalytics(data);
  }

  async getRecentScans(options: {
    page?: number;
    pageSize?: number;
    from?: string | null;
    to?: string | null;
    employeeId?: string | null;
    scope?: ScanAnalyticsScope;
    exportAll?: boolean;
  }): Promise<RecentScansPage> {
    const page = options.page ?? 1;
    const pageSize = options.pageSize ?? 10;
    const params: Record<string, string | number | boolean> = {
      page,
      pageSize,
      exportAll: options.exportAll ?? false,
    };
    if (options.from) params.from = options.from;
    if (options.to) params.to = options.to;
    if (options.employeeId) params.employeeId = options.employeeId;

    const scope = options.scope;
    const response = scope?.employeeScopeId
      ? await apiService.get(`/employee/${scope.employeeScopeId}/recent-scans`, {
          params,
          metadata: { skipGlobalLoading: true },
        } as any)
      : scope?.companyScopeId
        ? await apiService.get(`/company/${scope.companyScopeId}/recent-scans`, {
            params,
            metadata: { skipGlobalLoading: true },
          } as any)
        : scope?.individualScopeId
          ? await apiService.get(`/individual/${scope.individualScopeId}/recent-scans`, {
              params,
              metadata: { skipGlobalLoading: true },
            } as any)
          : await apiService.get(`/dashboard/RecentScans`, {
              params,
              metadata: { skipGlobalLoading: true },
            } as any);

    const data = (response as any)?.data || response;
    return normalizeRecentScansPage(data, page, pageSize);
  }
}

export const dashboardService = new DashboardService();
export default dashboardService;
